/**
 * Merci : les points de position de la rangée de suggestions du tiroir panier,
 * en mobile (`M-Slideshow_UI`, node Figma 494:20262).
 *
 * Le défilement est natif — `overflow-x` et `scroll-snap`, dans
 * `styles/_cart-drawer.scss` — et marche sans ce script. Celui-ci règle les
 * points que le serveur a rendus, un par carte (`snippets/merci-cart-upsell`) :
 * combien s'affichent, lequel est plein, et où mène un clic.
 *
 * **Un point par arrêt, et non par carte.** Les cartes font 140 sur un écran de
 * 375 : la rangée s'arrête quand la dernière touche le bord, et les dernières
 * cartes ne viennent jamais se caler à gauche. Quatre cartes donnent ainsi
 * trois arrêts — 0, 152, et la fin. Un point par carte en aurait laissé un
 * qui ne s'allume jamais. Les points en trop sont masqués ; s'il n'y a qu'un
 * arrêt — rien ne dépasse —, aucun ne s'affiche.
 *
 * **Le tiroir est réconcilié avec le serveur après chaque changement du
 * panier** (`morphSection` d'Horizon), qui remet les points à leur état
 * d'origine : tous masqués, aucun plein. Un `MutationObserver` voit passer ces
 * changements et relance le réglage ; les changements que le script fait
 * lui-même sont écartés (`takeRecords`), sans quoi il se relancerait sans fin.
 */
class MerciCartUpsell extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #mutations;
  /** @type {ResizeObserver | undefined} */
  #resize;
  #frame = 0;
  /** @type {number[]} */
  #stops = [];

  connectedCallback() {
    // `scroll` ne remonte pas, mais se capte en descendant : un seul écouteur
    // suffit, même quand la liste est remplacée.
    this.addEventListener('scroll', this.#schedule, true);
    this.addEventListener('click', this.#onClick);

    this.#mutations = new MutationObserver(this.#schedule);
    this.#mutations.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-current'],
    });

    this.#resize = new ResizeObserver(this.#schedule);
    this.#resize.observe(this);

    this.#schedule();
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.#schedule, true);
    this.removeEventListener('click', this.#onClick);
    this.#mutations?.disconnect();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const list = this.querySelector('.merci-cart-upsell__list');
    const wrapper = this.querySelector('.merci-cart-upsell__dots');
    if (!list || !wrapper) return;

    const dots = [...wrapper.querySelectorAll('.merci-cart-upsell__dot')];
    const items = [...list.children];
    const max = list.scrollWidth - list.clientWidth;

    // L'arrêt de chaque carte : là où `scroll-snap` la pose, contre le
    // rembourrage de la liste, et jamais au-delà de la fin.
    const padding = parseFloat(getComputedStyle(list).scrollPaddingInlineStart) || 0;
    const origin = list.getBoundingClientRect().left - list.scrollLeft + padding;
    const stops = items.map((item) =>
      Math.min(Math.max(item.getBoundingClientRect().left - origin, 0), Math.max(max, 0))
    );

    // Un point par arrêt distinct : une carte qui retombe sur l'arrêt de la
    // précédente n'en a pas.
    this.#stops = [];
    dots.forEach((dot, index) => {
      const stop = stops[index];
      const distinct = stop !== undefined && (index === 0 || stop - stops[index - 1] > 1);
      this.#set(dot, 'hidden', !distinct);
      if (distinct) this.#stops.push(stop);
      dot.dataset.stop = distinct ? String(this.#stops.length - 1) : '';
    });

    this.#set(wrapper, 'hidden', max <= 1 || this.#stops.length < 2);

    // Le point plein : l'arrêt le plus proche de la position courante.
    let active = 0;
    this.#stops.forEach((stop, index) => {
      if (Math.abs(stop - list.scrollLeft) < Math.abs(this.#stops[active] - list.scrollLeft)) active = index;
    });
    for (const dot of dots) {
      // `aria-current` informe les lecteurs d'écran et sert de crochet au CSS.
      const current = dot.dataset.stop === String(active);
      if (current !== dot.hasAttribute('aria-current')) {
        if (current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      }
    }

    this.#mutations?.takeRecords();
  }

  /**
   * @param {Element} element
   * @param {string} name
   * @param {boolean} on
   */
  #set(element, name, on) {
    if (on !== element.hasAttribute(name)) element.toggleAttribute(name, on);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const dot = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.merci-cart-upsell__dot') : null
    );
    const stop = this.#stops[Number(dot?.dataset.stop)];
    const list = this.querySelector('.merci-cart-upsell__list');
    if (stop === undefined || !list) return;

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({ left: stop, behavior: reduce ? 'auto' : 'smooth' });
  };
}

if (!customElements.get('merci-cart-upsell')) {
  customElements.define('merci-cart-upsell', MerciCartUpsell);
}
